/**
 * Dictionary of component type identifiers available in the editor.
 *
 * These string values are a wire contract, not a private enum: the admin's
 * projectors write them into every published page payload (`componentType`),
 * and the component registry dispatches renderers by them. Renaming or
 * removing a value silently breaks content that is already published — add
 * new identifiers, never repurpose existing ones. The `Home*` names are
 * aliases of the generic renderers (see componentRegistry), retained so
 * payloads using either name render identically.
 */
export const ComponentTypes = {
  PageHeader: "PageHeader",
  ArticleHero: "ArticleHero",
  ActivityGrid: "ActivityGrid",
  PaginatedActivityGrid: "PaginatedActivityGrid",
  PersonCardGrid: "PersonCardGrid",
  SectionHeading: "SectionHeading",
  LinkTileGrid: "LinkTileGrid",
  EventGrid: "EventGrid",
  ArticleGrid: "ArticleGrid",
  LatestArticleGrid: "LatestArticleGrid",
  Callout: "Callout",
  StatsGrid: "StatsGrid",
  SplitImageCallout: "SplitImageCallout",
  TextCardGrid: "TextCardGrid",
  LabeledTextGrid: "LabeledTextGrid",
  CompanyReportGrid: "CompanyReportGrid",
  BlogArticleGrid: "BlogArticleGrid",
  TextBlock: "TextBlock",
  Margin: "Margin",
  BannerImage: "BannerImage",
  FullWidthImage: "FullWidthImage",
  IconCardGrid: "IconCardGrid",
  ContactForm: "ContactForm",
  SeriesExplorer: "SeriesExplorer",
  KeyMetricsExplorer: "KeyMetricsExplorer",
  FxRatesExplorer: "FxRatesExplorer",
  // Generic, reusable names
  Hero: "Hero",
  NewsSection: "NewsSection",
  StatsSummary: "StatsSummary",
  AudienceTools: "AudienceTools",
  SearchBar: "SearchBar",
  PublicationsUpdates: "PublicationsUpdates",
  HomeHero: "HomeHero",
  HomeNews: "HomeNews",
  HomeBlueStats: "HomeBlueStats",
  HomeBlueTools: "HomeBlueTools",
  HomePubUpdatesSection: "HomePubUpdatesSection",
  TextWithSideImage: "TextWithSideImage",
  TextWithSidePanel: "TextWithSidePanel",
  SingleLink: "SingleLink",
  MapEmbed: "MapEmbed",
  HeroCarousel: "HeroCarousel",
  LegislationList: "LegislationList",
  NumberedList: "NumberedList",
  TwoColumnList: "TwoColumnList",
  Timeline: "Timeline",
  NewsletterSignup: "NewsletterSignup",
  NewsList: "NewsList",
  CtaBanner: "CtaBanner",
  AccordionSection: "AccordionSection",
  Coordonnees: "Coordonnees",
  BlueCallout: "BlueCallout",
  SeeAlso: "SeeAlso",
  DynamicComponents: "DynamicComponents",
  KeyIndicatorsBar: "KeyIndicatorsBar",
  InformationHub: "InformationHub",
  MarketsBoard: "MarketsBoard",
  DataRecordDetail: "DataRecordDetail",
  RichText: "RichText",
  Gallery: "Gallery",
  SpecimenMarquee: "SpecimenMarquee",
  VideoEmbed: "VideoEmbed",
  GovernorList: "GovernorList",
  GovernorHero: "GovernorHero",
  ViceGovernorList: "ViceGovernorList",
  BilletTabs: "BilletTabs",
  TextWithFigure: "TextWithFigure",
  BilletSecurity: "BilletSecurity",
  LayoutSection: "LayoutSection",
  SmartCardGrid: "SmartCardGrid",
  CtaButton: "CtaButton",
  Quote: "Quote",
  Divider: "Divider",
  JobOffersApply: "JobOffersApply",
  CareersHero: "CareersHero",
} as const;

/** Union type of all component identifiers. */
export type ComponentType = typeof ComponentTypes[keyof typeof ComponentTypes];

/** Human-readable display labels for each component type. */
export const ComponentTypeLabels: Record<ComponentType, string> = {
  PageHeader: "Page Header",
  ArticleHero: "Article Hero",
  ActivityGrid: "Activity Cards Grid",
  PaginatedActivityGrid: "Paginated Activity Grid",
  PersonCardGrid: "Person Cards Grid",
  SectionHeading: "Section Heading",
  LinkTileGrid: "Link Tile Grid",
  EventGrid: "Event Cards Grid",
  ArticleGrid: "Article Cards Grid",
  LatestArticleGrid: "Latest Article Cards Grid",
  Callout: "Callout Box",
  StatsGrid: "Stats KPIs Grid",
  SplitImageCallout: "Split Image + Callout",
  TextCardGrid: "Text Card Grid",
  LabeledTextGrid: "Labeled Text Grid",
  CompanyReportGrid: "Company Report Grid",
  BlogArticleGrid: "Blog Articles Grid",
  TextBlock: "Text Block",
  Margin: "Margin / Spacer",
  BannerImage: "Banner Image",
  FullWidthImage: "Full Width Image",
  IconCardGrid: "Icon Card Grid",
  ContactForm: "Contact Form",
  SeriesExplorer: "Series Explorer",
  KeyMetricsExplorer: "Key Metrics Explorer",
  FxRatesExplorer: "FX Rates Explorer",
  Hero: "Hero Section",
  NewsSection: "News Section",
  StatsSummary: "Stats Summary",
  AudienceTools: "Audience Tools",
  SearchBar: "Search Bar",
  PublicationsUpdates: "Publications and Updates",
  HomeHero: "Home Hero",
  HomeNews: "Home News",
  HomeBlueStats: "Home Blue Stats",
  HomeBlueTools: "Home Blue Tools",
  HomePubUpdatesSection: "Home Publications + Updates Section",
  TextWithSideImage: "Text With Side Image",
  TextWithSidePanel: "Text With Side Panel",
  SingleLink: "Single Link",
  MapEmbed: "Map Embed (Google Maps)",
  HeroCarousel: "Hero Carousel (bannière animée)",
  LegislationList: "Legislation / Document List",
  NumberedList: "Numbered List",
  TwoColumnList: "Two-Column Text List",
  Timeline: "Frise chronologique",
  NewsletterSignup: "Newsletter Signup",
  NewsList: "News List",
  CtaBanner: "CTA Banner",
  AccordionSection: "Accordion Section",
  Coordonnees: "Coordonnées",
  BlueCallout: "Blue Callout",
  SeeAlso: "Voir aussi",
  DynamicComponents: "Dynamic Components",
  KeyIndicatorsBar: "Key Indicators Bar",
  InformationHub: "Information Hub (Publications + Communiqués)",
  MarketsBoard: "Markets Board (Exchange + Inflation + Interest)",
  DataRecordDetail: "Data Record Detail (dated record page)",
  RichText: "Rich Text (editorial body HTML)",
  Gallery: "Gallery (captioned image grid)",
  SpecimenMarquee: "Bande de spécimens (vitrine défilante des documents de sécurité)",
  VideoEmbed: "Video Embed (responsive 16:9 player)",
  GovernorList: "Governor List (BCC governors history grid)",
  GovernorHero: "Governor Hero (portrait + title for detail pages)",
  ViceGovernorList: "Vice-Governor List (per-governor vice-governors section)",
  BilletTabs: "Billet Tabs (denomination quick switcher)",
  TextWithFigure: "Text With Figure (intro + labelled items + side image)",
  BilletSecurity: "Billet Security (numbered security elements grid)",
  LayoutSection: "Container (grille 12 colonnes responsive)",
  SmartCardGrid: "Grille de cartes (cartes adaptatives selon les propriétés remplies)",
  CtaButton: "Bouton (appel à l'action autonome)",
  Quote: "Citation (exergue avec attribution)",
  Divider: "Séparateur (ligne ou points entre les blocs)",
  JobOffersApply: "Offres d'emploi + candidature (liste dynamique et formulaire)",
  CareersHero: "Héros recrutement (fond duotone + photo détourée + texte)",
};


